import React, { Suspense, useEffect, useState } from "react";
import { Outlet } from "react-router";
import { useLoaderData, useParams } from "react-router";
import { TreePositions, getFieldTrees } from "../../../../../API/trees";

import useShowTrees from "./useShowTrees";
import { useTreesLastModified } from "./useTreesLastModified";
import { usePhotosDrivingDay } from "./usePhotosDrivingDay";
import SensorImageSnackbar from "./SensorImageSnackbar";
import { useSensorImageNotice } from "./useSensorImageNotice";
import { TreeLayer } from "../../../../../Component/Map/Layers/TreeLayer";
import { useLoading } from "../../../../../Component/LoadingSpinner/LoadingContext";
import {
  TreePhotoPositions,
  TreeScoutData,
  getPhotosByDrivingDay,
} from "../../../../../API/drivingDay";
import { PhotoLayer } from "../../../../../Component/Map/Layers/PhotoLayer";
import { DateTime } from "luxon";
import { GetFieldParams } from "../../../../../API/route-params.types";

function SelectFieldFallback() {
  const { setLoading } = useLoading();
  const treeScoutData = useLoaderData() as TreeScoutData;

  useEffect(() => {
    setLoading(true);
    return () => {
      setLoading(false);
    };
  }, [treeScoutData?.drivingDays]);

  return <></>;
}

export default function SelectedField(): React.ReactNode {
  const params = useParams();
  const { setLoading } = useLoading();

  const [showTrees] = useShowTrees();
  const [photosDrivingDay] = usePhotosDrivingDay();
  const setNoRawSensorImages = useSensorImageNotice(
    (state) => state.setNoRawSensorImages,
  );
  const setTreesLastModified = useTreesLastModified(
    (state) => state.setLastModified,
  );

  const [trees, setTrees] = useState<TreePositions | undefined>();
  const [photo, setPhoto] = useState<
    Promise<{ url: string; timestamp: DateTime | string }> | undefined
  >(undefined);
  const [photosGeojson, setPhotosGeojson] = useState<
    TreePhotoPositions | undefined
  >(undefined);

  useEffect(() => {
    if (photosDrivingDay) {
      setLoading(true);
      getPhotosByDrivingDay(photosDrivingDay)
        .then((geojson: TreePhotoPositions) => {
          setPhotosGeojson(geojson);
          setNoRawSensorImages(geojson.features.length === 0);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Failed to load photo positions", error);
          setPhotosGeojson(undefined);
          setNoRawSensorImages(false);
          setLoading(false);
        });
    } else {
      setPhotosGeojson(undefined);
      setNoRawSensorImages(false);
    }
  }, [photosDrivingDay]);

  useEffect(() => {
    let cancelled = false;

    setTrees(undefined);
    setTreesLastModified(null);

    if (showTrees) {
      setLoading(true);
      getFieldTrees({ params } as GetFieldParams)
        .then(({ positions, lastModified }) => {
          if (cancelled) {
            return;
          }
          setTrees(positions);
          setTreesLastModified(lastModified);
          setLoading(false);
        })
        .catch((error) => {
          if (cancelled) {
            return;
          }
          console.error("Failed to load field trees", error);
          setLoading(false);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [showTrees, params.fieldId]);

  return (
    <Suspense fallback={<SelectFieldFallback />}>
      <>
        <Outlet />
        {showTrees && trees?.features?.length && <TreeLayer trees={trees} />}
        {photosGeojson?.features.length && (
          <PhotoLayer
            photos={photosGeojson}
            active={photo}
            setActive={setPhoto}
          />
        )}
        <SensorImageSnackbar />
      </>
    </Suspense>
  );
}
